import React, { useState, useEffect } from "react";
import axios from "axios";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Heart } from "lucide-react";

import { useNotification } from "../../contexts/NotificationContext";
import { formatWithSystemLocale } from '../../utils/dateUtils';
import { getApiUrl } from "../../config/api";

// Utility function to format date without timezone conversion
const formatDate = (dateString) => {
  if (!dateString) return 'No date';
  // Split the date string and format it directly to avoid timezone issues
  const [year, month, day] = dateString.split('T')[0].split('-');
  return `${month}/${day}/${year}`;
};

export default function Vaccinations() {
  const { id: horseId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { showSuccess, showError, showWarning } = useNotification();
  const [records, setRecords] = useState([]);
  const [form, setForm] = useState({
    cycle_date: "",
    foaling_date: "",
    notes: "",
    amount: ""
  });

  useEffect(() => {
    fetchRecords();
  }, []);

  // Check for edit parameter in URL
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const editId = urlParams.get('edit');
    if (editId && records.length > 0) {
      const recordToEdit = records.find(rec => rec.id.toString() === editId);
      if (recordToEdit) {
        startEdit(recordToEdit);
        // Clean up URL by removing the edit parameter
        navigate(`/horses/${horseId}/breedings`, { replace: true });
      }
    }
  }, [records, location.search, navigate, horseId]);

  const fetchRecords = async () => {
    try {
      const res = await axios.get(getApiUrl(`/api/horses/${horseId}/breedings`), {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      setRecords(res.data);
    } catch (err) {
      console.error("Fetch error:", err);
    }
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const cleanAmount = (amount) => {
    if (!amount) return 0;
    // Convert to number (now numeric from database)
    return typeof amount === 'number' ? amount : parseFloat(amount) || 0;
  };

  const formatAmountDisplay = (amount) => {
    if (!amount) return null;
    // Format as currency (now numeric from database)
    const cleanValue = typeof amount === 'number' ? amount : parseFloat(amount) || 0;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(cleanValue);
  };

  const handleSubmit = async () => {
    try {
      const endpoint = `/api/horses/${horseId}/breeding`;
      
      const cleanForm = {
        ...form,
        amount: cleanAmount(form.amount)
      };
      
      await axios.post(getApiUrl(endpoint), cleanForm, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      setForm({ cycle_date: "", foaling_date: "", notes: "", amount: "" });
      showSuccess("Breeding record added successfully!");
      fetchRecords();
    } catch (err) {
      console.error("Submit error:", err);
      console.error("Error response:", err.response);
      if (err.response?.data?.error) {
        showError(err.response.data.error);
      } else {
        showError("Failed to add breeding record");
      }
    }
  };

  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [showForm, setShowForm] = useState(false);

 const startEdit = (v) => {
  setEditingId(v.id);
  setEditData({
    cycle_date: v.cycle_date ? v.cycle_date.split("T")[0] : "",
    foaling_date: v.foaling_date ? v.foaling_date.split("T")[0] : "",
    notes: v.notes || "",
    amount: v.Amount || v.amount || "",
  });
};
  const saveEdit = async (id) => {
    try {
      const cleanEditData = {
        ...editData,
        amount: cleanAmount(editData.amount)
      };
      
      await axios.put(getApiUrl(`/api/horses/${horseId}/breeding/${id}`), cleanEditData, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      setEditingId(null);
      showSuccess("Breeding record updated successfully!");
      fetchRecords();
    } catch (err) {
      console.error("Update error:", err);
      if (err.response?.data?.error) {
        showError(err.response.data.error);
      } else {
        showError("Failed to update breeding record");
      }
    }
  };

  const cancelEdit = () => {
    setEditingId(null);
  };

  const handleDelete = async (recordId) => {
    try {
      await axios.delete(getApiUrl(`/api/horses/${horseId}/breeding/${recordId}`), {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      showSuccess("Breeding record deleted successfully!");
      fetchRecords();
    } catch (err) {
      console.error("Delete error:", err);
      showError("Failed to delete breeding record");
    }
  };

  const goBack = () => navigate("/horses");

  return (
    <div className="p-6 max-w-4xl mx-auto">
      
      <button onClick={goBack} className="mb-4 px-4 py-2 bg-gray-100 border rounded hover:bg-gray-200 text-sm">
        ← Back to Horses
      </button>
      <h2 className="text-2xl font-bold mb-6 flex items-center">
        <Heart className="mr-2 text-red-500" /> Breeding Records
      </h2>

      {!editingId && (
        <div className="bg-white shadow p-6 rounded mb-8">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Add Breeding Record</h3>
            <button
              onClick={() => setShowForm(!showForm)}
              className="text-sm text-blue-600 hover:underline focus:outline-none"
            >
              {showForm ? "Hide" : "Add"}
            </button>
          </div>

          {showForm && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Cycle Date *</label>
              <input
                type="date"
                name="cycle_date"
                placeholder="Cycle Date"
                value={form.cycle_date}
                onChange={handleChange}
                className="border px-3 py-2 rounded w-full"
                required
              />
              <p className="text-xs text-gray-500 mt-1">When the breeding cycle began</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Expected Foaling Date</label>
              <input
                type="date"
                name="foaling_date"
                value={form.foaling_date}
                onChange={handleChange}
                className="border px-3 py-2 rounded w-full"
              />
              <p className="text-xs text-gray-500 mt-1">Expected foaling date</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Cost</label>
              <input
                name="amount"
                placeholder="0.00"
                value={form.amount}
                onChange={handleChange}
                className="border px-3 py-2 rounded w-full"
              />
              <p className="text-xs text-gray-500 mt-1">Breeding cost</p>
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <input
                name="notes"
                placeholder="Notes (optional)"
                value={form.notes}
                onChange={handleChange}
                className="border px-3 py-2 rounded w-full"
              />
              <p className="text-xs text-gray-500 mt-1">Any additional information about the breeding</p>
            </div>
          </div>
        )}

          {showForm && (
            <button
              onClick={handleSubmit}
              className="mt-4 bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700"
            >
              Save Breeding
            </button>
          )}
        </div>
      )}

      <div className="bg-white shadow p-6 rounded">
        <h3 className="text-lg font-semibold mb-4">Breeding History</h3>
        {records.length === 0 ? (
          <p className="text-gray-500">No records yet.</p>
        ) : (
        <ul className="divide-y divide-gray-200">
          {records.map((rec) => (
            <li key={rec.id} className="py-4 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
              {editingId === rec.id ? (
                <div className="w-full sm:flex-grow">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Cycle Date *</label>
                      <input
                        className="border px-2 py-1 rounded w-full"
                        type="date"
                        name="cycle_date"
                        value={editData.cycle_date}
                        onChange={(e) => setEditData({ ...editData, cycle_date: e.target.value })}
                        placeholder="Cycle Date"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Expected Foaling Date</label>
                      <input
                        className="border px-2 py-1 rounded w-full"
                        type="date"
                        name="foaling_date"
                        value={editData.foaling_date}
                        onChange={(e) => setEditData({ ...editData, foaling_date: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Cost</label>
                      <input
                        className="border px-2 py-1 rounded w-full"
                        name="amount"
                        value={editData.amount}
                        onChange={(e) => setEditData({ ...editData, amount: e.target.value })}
                        placeholder="0.00"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                      <input
                        className="border px-2 py-1 rounded w-full"
                        name="notes"
                        value={editData.notes}
                        onChange={(e) => setEditData({ ...editData, notes: e.target.value })}
                        placeholder="Notes"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2 mt-4">
                    <button
                      onClick={() => saveEdit(rec.id)}
                      className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
                    >
                      Save
                    </button>
                    <button
                      onClick={cancelEdit}
                      className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div>
                    <p className="font-medium">Cycle Date: {rec.cycle_date ? formatDate(rec.cycle_date) : 'Not set'}</p>
                    <p className="text-sm text-gray-600">
                      Expected Foaling: {rec.foaling_date ? formatDate(rec.foaling_date) : 'Not set'}
                    </p>
                    {(rec.Amount || rec.amount) && (
                      <p className="text-sm text-gray-600">
                        Cost: {formatAmountDisplay(rec.Amount || rec.amount)}
                      </p>
                    )}
                    {rec.notes && <p className="text-sm text-gray-500 italic">{rec.notes}</p>}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => startEdit(rec)}
                      className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(rec.id)}
                      className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-sm"
                    >
                      Delete
                    </button>
                  </div>
                </>
              )}
            </li>
          ))}
        </ul>

        )}
      </div>
    </div>
  );
}
